import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { MapContainer, TileLayer, Polygon, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';

import PollWindow from './PollWindow/PollWindow';
import Fader from '../Loaders/Fader';

import { saveHexagons, loadHexagons } from '../../../util/LocalDb';
import { getPollData, getAllRidingShapes, getRidingShapes } from '../../../http/pollsAPI';

import './Map.scss';

const FitBounds = ({ polygons }) => {
  const map = useMap();

  useEffect(() => {
    if (!polygons || polygons.length === 0) return;

    const allCoords = polygons.flatMap((polygon) => polygon.coordinates);

    const bounds = L.latLngBounds(allCoords);
    map.fitBounds(bounds, { padding: [20, 20] });
  }, [polygons, map]);

  return null;
};

const Map = ({ pollId }) => {
  const config = useSelector((state) => state.config.config);

  const [polygons, setPolygons] = useState([]);
  const [activePoll, setActivePoll] = useState({ id: 0, coordinates: [], title: '' });
  const [ridingPoly, setRidingPoly] = useState([]);
  const [cp, setCp] = useState([]);
  const [mapData, setMapData] = useState(false);
  const [polygonData, setPolygonData] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchOrLoadData = async () => {
    try {
      const cached = await loadHexagons();
      if (cached && cached.length) {
        setPolygons(cached);
      } else {
        const res = await getAllRidingShapes(config);
        const data = res?.data?.rows || [];
        setPolygons(data);
        await saveHexagons(data);
      }
    } catch (error) {
      console.error('Error fetching or loading polygons:', error);
    }
  };

  const fetchOrLoadRidingData = async () => {
    try {
      const res = await getRidingShapes(config, pollId);

      setPolygons(res.data.rows.riding_polls);
      setCp(res.data.rows.riding[0].cp);
      setRidingPoly(res.data.rows.riding[0].coordinates);
      setActivePoll(res.data.rows.poll[0]);

      setMapData(true);
    } catch (err) {
      console.error('Error fetching or loading polygons for riding:', err);
    }
  };

  useEffect(() => {
    if (!config) {
      setTimeout(() => {
        if (pollId) {
          fetchOrLoadRidingData();
        } else {
          fetchOrLoadData();
        }
      }, 1000);
    }

    if (pollId) {
      fetchOrLoadRidingData();
    } else {
      fetchOrLoadData();
    }
  }, [config, pollId]);

  const handlePolygonClick = async (polygonId) => {
    try {
      setIsModalOpen(true);
      const res = await getPollData(config, polygonId);
      setPolygonData(res?.data?.rows?.[0] || null);
    } catch (err) {
      console.error(`Failed to load poll data ${polygonId}:`, err);
    }
  };

  const closePollWindow = () => {
    setIsModalOpen(false);
    setPolygonData(null);
  };

  if (pollId && !mapData) {
    return null;
  }

  return (
    <div>
      <div>
        {polygons.length > 0 ? (
          <div className="polygon-map">
            <MapContainer style={{ width: '100%', height: '100%' }} zoom={13} center={cp.length > 0 ? cp : [0, 0]}>
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {ridingPoly.length > 0 ? <Polygon positions={ridingPoly} pathOptions={{ color: 'red' }} /> : ''}
              {polygons.map((polygon) => (
                <Polygon
                  key={polygon.id}
                  positions={polygon.coordinates}
                  pathOptions={!pollId || (pollId && polygon.id === activePoll.id) ? {} : { color: 'rgba(0,0,0,0.23)' }}
                  eventHandlers={{ click: () => handlePolygonClick(polygon.id) }}
                >
                  <Tooltip sticky>{polygon.title || `Polygon ${polygon.id}`}</Tooltip>
                </Polygon>
              ))}
              <FitBounds polygons={polygons} />
            </MapContainer>
          </div>
        ) : (
          <div className="sentiment-charts">
            <Fader />
          </div>
        )}
      </div>

      {isModalOpen && <PollWindow data={polygonData} isOpen={isModalOpen} onCloseHandler={closePollWindow} />}
    </div>
  );
};

export default Map;
